import { apiFetch } from "./api";
import type { Paginated } from "./platform-api";

export type EmployeeStatus = "ACTIVE" | "INACTIVE";
export type MobileAppStatus = "NOT_INSTALLED" | "INSTALLED" | "ACTIVE" | "INACTIVE";

export interface Employee {
  id: string;
  tenantId: string;
  empCode: string;
  name: string;
  email: string | null;
  phone: string | null;
  /** Managed department (ADR-0008): id + resolved display name. */
  departmentId: string | null;
  department: string | null;
  /** Full org path "Company › Division › Unit" (ADR-0010). */
  departmentPath: string | null;
  role: string | null;
  /** Staff category; reporting only. */
  employmentType: string | null;
  /** Default working pattern (FEAT-2026-017); null follows the site. */
  shiftId: string | null;
  shiftName: string | null;
  primarySiteId: string | null;
  primarySiteName: string | null;
  managerId: string | null;
  managerName: string | null;
  status: EmployeeStatus;
  mobileAppStatus: MobileAppStatus;
  gpsEnabled: boolean;
  /** True when the employee has a Clerk identity linked (i.e. has been invited). */
  mobileAuthLinked: boolean;
  /** True when the employee has stored a home location (WFH). */
  homeLocationSet: boolean;
  /** Sensitive PII — present only for TENANT_OWNER / HR_MANAGER callers. */
  home?: {
    address: string | null;
    latitude: number;
    longitude: number;
    radiusM: number | null;
    updatedAt: string | null;
  } | null;
  lastActivityAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListEmployeesQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: EmployeeStatus;
  departmentId?: string;
  /** Several departments, each with its sub-units (FEAT-2026-030). */
  departmentIds?: string[];
  employmentType?: string;
  shiftId?: string | null;
  primarySiteId?: string;
  /** Several primary sites (FEAT-2026-030). */
  primarySiteIds?: string[];
}

export interface CreateEmployeeBody {
  empCode: string;
  name: string;
  email?: string;
  phone?: string;
  /** Managed department id (ADR-0008). */
  departmentId?: string;
  role?: string;
  employmentType?: string;
  shiftId?: string | null;
  primarySiteId?: string;
  /** On update: omit = no change, null = remove the manager. */
  managerId?: string | null;
  status?: EmployeeStatus;
  mobileAppStatus?: MobileAppStatus;
  gpsEnabled?: boolean;
}

export type UpdateEmployeeBody = Partial<CreateEmployeeBody>;

/** The list's filters, without paging. */
export type EmployeeSummaryQuery = Pick<
  ListEmployeesQuery,
  "search" | "status" | "departmentId" | "departmentIds" | "primarySiteId" | "primarySiteIds"
>;

/** Headline counts over every employee the filters match, not one page. */
export interface EmployeeSummary {
  total: number;
  active: number;
  inactive: number;
  mobileApp: number;
}

export interface BulkImportEmployeeRow {
  empCode: string;
  name: string;
  email?: string;
  phone?: string;
  department?: string;
  role?: string;
  primarySiteCode?: string;
  managerCode?: string;
  status?: EmployeeStatus;
}

export interface BulkImportEmployeesResult {
  /** Each row creates an employee record only — invite separately to enable mobile auth. */
  created: Array<{ empCode: string; name: string }>;
  skipped: Array<{ empCode: string; reason: string }>;
  totalProcessed: number;
}

/**
 * Result of `invite` / `resetMobilePassword`. Sign-in is by email only
 * (ADR-0017), so the API refuses both for an employee with no email.
 *   - first invite: `invitationSent=true`, `inviteUrl` set, `linkedToExisting=false`.
 *   - Clerk identity already exists (e.g. the employee is also a dashboard
 *     User): `invitationSent=false`, `inviteUrl=null`, `linkedToExisting=true`.
 *     They keep their existing password.
 *   - reset on an already-linked account: `invitationSent=true`,
 *     `inviteUrl=null` — user uses "Forgot password" themselves.
 */
export interface MobileInviteResult {
  employee: Employee;
  invitationSent: boolean;
  inviteUrl: string | null;
  linkedToExisting?: boolean;
}

function toQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    // Lists go as `a,b` (FEAT-2026-030); an empty one is no filter.
    if (Array.isArray(v) && v.length === 0) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export const employeesApi = {
  list: (q: ListEmployeesQuery = {}) =>
    apiFetch<Paginated<Employee>>(`/employees${toQuery(q as Record<string, unknown>)}`),

  summary: (q: EmployeeSummaryQuery = {}) =>
    apiFetch<EmployeeSummary>(`/employees/summary${toQuery(q as Record<string, unknown>)}`),

  get:(id: string) => apiFetch<Employee>(`/employees/${id}`),

  create: (body: CreateEmployeeBody) =>
    apiFetch<Employee>(`/employees`, { method: "POST", body }),

  update: (id: string, body: UpdateEmployeeBody) =>
    apiFetch<Employee>(`/employees/${id}`, { method: "PATCH", body }),

  disable: (id: string) =>
    apiFetch<{ id: string; status: EmployeeStatus }>(`/employees/${id}`, { method: "DELETE" }),

  bulkImport: (rows: BulkImportEmployeeRow[]) =>
    apiFetch<BulkImportEmployeesResult>(`/employees/import`, {
      method: "POST",
      body: { rows },
    }),

  /** First-time mobile auth provisioning (Clerk user + invitation). */
  invite: (id: string) =>
    apiFetch<MobileInviteResult>(`/employees/${id}/invite`, { method: "POST" }),

  /** Re-issue mobile credentials. */
  resetMobilePassword: (id: string) =>
    apiFetch<MobileInviteResult>(`/employees/${id}/reset-mobile-password`, {
      method: "POST",
    }),
};
